import { type ReactNode, type RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { cn } from "../utils/general";
import { Panel, type PanelImperativeHandle } from "./panel";

const isMissingPanelGroupError = (error: unknown) =>
  error instanceof Error && /^Group .+ not found$/.test(error.message);

const clearStalePanelRef = (panelRef: RefObject<PanelImperativeHandle | null>, panel: PanelImperativeHandle) => {
  if (panelRef.current === panel) {
    panelRef.current = null;
  }
};

// Run an imperative panel command (collapse/expand) defensively: a handle whose
// group has been torn down throws "Group ... not found". Swallow that, drop the
// stale handle, and let the next mount re-attach — anything else rethrows.
const runPanelCommand = (command: () => void, onMissingPanelGroup?: () => void) => {
  try {
    command();
  } catch (error) {
    if (isMissingPanelGroupError(error)) {
      onMissingPanelGroup?.();
      return;
    }

    throw error;
  }
};

// A collapsed panel sits at its `collapsedSize` ("0%"). We treat the panel as
// collapsed strictly from its *rendered* width, not the library's collapsed
// flag or an optimistic React flag. Both of those can drift from what is on
// screen (a stale/cleared handle freezes the flag; an optimistic set fires
// before the panel actually moves), which would leave a visibly-wide panel
// marked `inert` — dead to clicks. The rendered width cannot drift. A real
// panel is always far wider than this; a collapsed one clips to ~0.
const COLLAPSED_WIDTH_PX = 8;

export interface CollapsiblePanelController {
  /** True when the panel is rendered at (near) zero width. */
  isCollapsed: boolean;
  /** Collapse the panel to its `collapsedSize`. */
  collapse: () => void;
  /** Expand the panel back to its previous size. */
  expand: () => void;
  /** Toggle between collapsed and expanded. */
  toggle: () => void;
  /** @internal Wiring consumed by `<CollapsiblePanel>`; not for direct use. */
  readonly panelRef: RefObject<PanelImperativeHandle | null>;
  /** @internal Callback ref consumed by `<CollapsiblePanel>` to measure width. */
  readonly measureRef: (element: HTMLElement | null) => void;
}

/**
 * Owns the collapsed state for a single `<CollapsiblePanel>`. The returned
 * controller is the seam between the panel, the parent (which reads
 * `isCollapsed`), and any policy that drives `collapse()`/`expand()`.
 */
export const useCollapsiblePanelController = (): CollapsiblePanelController => {
  const panelRef = useRef<PanelImperativeHandle | null>(null);
  const [contentElement, setContentElement] = useState<HTMLElement | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Source of truth: the content's rendered width, observed from the DOM. This
  // tracks what the user actually sees regardless of the library's handle state
  // or any optimistic flag, so the a11y guard can never strand a visible panel.
  useLayoutEffect(() => {
    if (!contentElement) {
      return;
    }

    const measure = () => {
      setIsCollapsed(contentElement.getBoundingClientRect().width < COLLAPSED_WIDTH_PX);
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(contentElement);
    return () => observer.disconnect();
  }, [contentElement]);

  const collapse = useCallback(() => {
    const panel = panelRef.current;
    if (panel) {
      runPanelCommand(
        () => panel.collapse(),
        () => clearStalePanelRef(panelRef, panel)
      );
    }
  }, []);

  const expand = useCallback(() => {
    const panel = panelRef.current;
    if (panel) {
      runPanelCommand(
        () => panel.expand(),
        () => clearStalePanelRef(panelRef, panel)
      );
    }
  }, []);

  const toggle = useCallback(() => {
    if (isCollapsed) {
      expand();
    } else {
      collapse();
    }
  }, [collapse, expand, isCollapsed]);

  return useMemo(
    () => ({ isCollapsed, collapse, expand, toggle, panelRef, measureRef: setContentElement }),
    [collapse, expand, isCollapsed, toggle]
  );
};

export interface CollapsiblePanelProps {
  controller: CollapsiblePanelController;
  id: string;
  children: ReactNode;
  /** Class applied to the panel box (forwarded to the underlying Panel). */
  className?: string;
  /** Class applied to the inert content wrapper inside the panel. */
  contentClassName?: string;
  minSize?: number | string;
  defaultSize?: number | string;
  collapsedSize?: number | string;
}

/**
 * A resizable panel that can collapse to zero width while staying mounted.
 * Applies `aria-hidden` + `inert` to its content whenever it is rendered at
 * ~zero width, so a collapsed-but-mounted panel never leaks focusable or
 * screen-reader content — and a visibly-wide panel is never made inert.
 */
export const CollapsiblePanel = ({
  controller,
  id,
  children,
  className,
  contentClassName,
  minSize,
  defaultSize,
  collapsedSize = "0%",
}: CollapsiblePanelProps) => {
  const { isCollapsed, panelRef, measureRef } = controller;

  return (
    <Panel
      id={id}
      panelRef={panelRef}
      minSize={minSize}
      defaultSize={defaultSize}
      collapsedSize={collapsedSize}
      collapsible
      className={className}
    >
      <div
        ref={measureRef}
        className={cn("h-full", contentClassName)}
        aria-hidden={isCollapsed || undefined}
        inert={isCollapsed || undefined}
      >
        {children}
      </div>
    </Panel>
  );
};
